import React, { useState } from 'react'
import { WizardState, ExamOption } from '../Index'
import { buildDefaultTestTitle, isAutoGeneratedTitle } from '../titleHelper'
import { BookOpen, Layers, FileText, Zap, ChevronDown, ChevronUp, Info } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface Props {
  state: WizardState
  updateState: (patch: Partial<WizardState>) => void
  exams: ExamOption[]
}

const DEFAULT_TYPE_ICONS: Record<string, any> = {
  full_mock: Layers,
  sectional: BookOpen,
  chapter_test: FileText,
  practice: Zap,
}

const DIFFICULTY_LEVELS = [
  { key: 'easy',   label: 'Easy' },
  { key: 'medium', label: 'Medium' },
  { key: 'hard',   label: 'Hard' },
  { key: 'mixed',  label: 'Mixed (auto-distribute)' },
]

export default function StepTestType({ state, updateState, exams }: Props) {
  const [expandedSectionsMap, setExpandedSectionsMap] = useState<Record<string | number, boolean>>({})
  const selectedExam = exams.find(e => e.id === state.exam_id)
  const examTestTypes = selectedExam?.test_types || []

  const toggleCardSections = (typeKey: string | number) => {
    setExpandedSectionsMap(prev => ({ ...prev, [typeKey]: !prev[typeKey] }))
  }

  // Fallback types if exam doesn't have custom ones configured
  const displayTypes = examTestTypes.length > 0
    ? examTestTypes
    : [
        {
          id: 1,
          name: 'Full Mock Test',
          test_type_key: 'full_mock',
          total_questions: 100,
          duration_minutes: 60,
          positive_marks: 1.0,
          negative_marks: 0.25,
          negative_marking_enabled: true,
          difficulty_applicable: false,
          description: `Mirrors the official examination pattern for ${selectedExam?.name || 'this exam'}.`,
          sort_order: 1
        },
        {
          id: 2,
          name: 'Sectional Test',
          test_type_key: 'sectional',
          total_questions: 35,
          duration_minutes: 20,
          positive_marks: 1.0,
          negative_marks: 0.25,
          negative_marking_enabled: true,
          difficulty_applicable: true,
          description: 'Focus on one or more specific subject sections.',
          sort_order: 2
        },
        {
          id: 3,
          name: 'Chapter Test',
          test_type_key: 'chapter_test',
          total_questions: 30,
          duration_minutes: 45,
          positive_marks: 1.0,
          negative_marks: 0.25,
          negative_marking_enabled: true,
          difficulty_applicable: true,
          description: 'Deep dive quiz into selected syllabus chapters.',
          sort_order: 3
        },
        {
          id: 4,
          name: 'Daily Practice Set',
          test_type_key: 'practice',
          total_questions: 20,
          duration_minutes: null,
          positive_marks: 1.0,
          negative_marks: 0.0,
          negative_marking_enabled: false,
          difficulty_applicable: false,
          description: 'Untimed practice mode without negative marking.',
          sort_order: 4
        }
      ]

  const activeSelected =
    (state.exam_test_type_id ? displayTypes.find(t => t.id === state.exam_test_type_id) : null) ||
    (state.testType ? displayTypes.find(t => t.test_type_key === state.testType) : null) ||
    displayTypes[0]
  const showDifficulty = activeSelected ? ['sectional', 'chapter_test'].includes(activeSelected.test_type_key) : false

  const handleSelectType = (typeItem: any) => {
    const totalQ = typeItem.total_questions || state.totalQuestions || 100
    const posMarks = typeItem.positive_marks ?? (state.markingScheme?.positive_marks || 1.0)
    const negMarks = typeItem.negative_marks ?? 0.25
    const isPractice = typeItem.test_type_key === 'practice'
    const negEnabled = !isPractice && negMarks > 0
    const isUntimed = isPractice || typeItem.duration_minutes === null
    const durationMins = isUntimed
      ? null
      : (typeItem.duration_minutes !== undefined ? typeItem.duration_minutes : state.durationMinutes)

    const examTitle = buildDefaultTestTitle({
      examName: selectedExam?.name,
      examCode: selectedExam?.code,
      testTypeName: typeItem.name,
      testTypeKey: typeItem.test_type_key
    })

    const matchedRule = typeItem.marking_rule || selectedExam?.marking_rules?.find(r => r.id === typeItem.exam_marking_rule_id)

    const timerMode = isUntimed ? 'none' : ((typeItem as any).timer_type || 'global')
    const adminNote = (typeItem as any).instructions_html || state.preTestInstructionsHtml || ''
    const blueprintDesc = typeItem.description || state.description || ''

    const ttSections = (typeItem.sections || []).map((sec: any) => ({
      ...sec,
      _selected: true
    }))

    updateState({
      testType: typeItem.test_type_key,
      exam_test_type_id: typeItem.id || null,
      blueprintSections: ttSections,
      resolvedSectionParams: [],
      timerType: timerMode,
      noDuration: isUntimed,
      description: blueprintDesc,
      preTestInstructionsHtml: adminNote,
      difficultyLevel: null,
      totalQuestions: totalQ,
      durationMinutes: durationMins,
      totalMarks: totalQ * posMarks,
      title: (!state.title || isAutoGeneratedTitle(state.title, selectedExam?.name, selectedExam?.code)) ? examTitle : state.title,
      subjectWeightages: [],
      markingScheme: {
        ...state.markingScheme,
        selected_rule_id: matchedRule ? matchedRule.id : (state.markingScheme?.selected_rule_id || 'custom'),
        rule_name: matchedRule ? matchedRule.name : (typeItem.name || 'Custom Marking Scheme'),
        positive_marks: posMarks,
        negative_marks: negEnabled ? negMarks : 0,
        negative_marking_enabled: negEnabled,
        scoring_strategy: matchedRule?.scoring_strategy || 'standard',
        scoring_params: matchedRule?.scoring_params || { correct: posMarks, wrong: -negMarks }
      }
    })
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      <div>
        <h2 className="text-base sm:text-lg font-bold text-foreground leading-tight">
          Choose Test Type {selectedExam ? `for ${selectedExam.name}` : ''}
        </h2>
        <p className="text-xs sm:text-sm text-muted-foreground mt-1">
          Select from the configured test type blueprints for this exam.
        </p>
      </div>

      {/* Type Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 sm:gap-4">
        {displayTypes.map(type => {
          const Icon = DEFAULT_TYPE_ICONS[type.test_type_key] || Layers
          const isSelected = activeSelected
            ? (type.id != null ? activeSelected.id === type.id : activeSelected.test_type_key === type.test_type_key)
            : (state.exam_test_type_id ? state.exam_test_type_id === type.id : state.testType === type.test_type_key)
          return (
            <button
              key={type.id || type.test_type_key}
              id={`test-type-${type.id ?? type.test_type_key}`}
              type="button"
              onClick={() => handleSelectType(type)}
              className={`flex flex-col items-start gap-3 p-4 sm:p-5 rounded-2xl border text-left transition-all duration-200 w-full ${
                isSelected
                  ? 'border-primary bg-primary/5 ring-1 ring-primary shadow-xs'
                  : 'border-border bg-card hover:border-primary/40 hover:bg-muted/40'
              }`}
            >
              {/* 1. Title Row: Icon + Title */}
              <div className="flex items-center gap-2.5 w-full">
                <div className={`p-2 rounded-xl shrink-0 ${isSelected ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                  <Icon className="h-4.5 w-4.5" />
                </div>
                <h3 className={`font-bold text-sm sm:text-base leading-snug ${isSelected ? 'text-primary' : 'text-foreground'} break-words flex-1`}>
                  {type.name}
                </h3>
              </div>

              {/* 2. Full Width Description */}
              {type.description && (
                <p className="text-xs text-muted-foreground leading-relaxed break-words w-full">
                  {type.description}
                </p>
              )}

              {/* 2. Combined Tags / Badges */}
              <div className="flex items-center gap-1.5 flex-wrap w-full">
                <Badge variant="outline" className="text-[10px] font-mono font-bold uppercase tracking-wider bg-muted/60 text-muted-foreground border-border/60">
                  {type.test_type_key.replace('_', ' ')}
                </Badge>
                <Badge variant="outline" className="text-[10px] uppercase font-semibold bg-muted/40 text-muted-foreground border-border/60">
                  ⏱️ {(type as any).timer_type === 'none' || type.duration_minutes == null || type.test_type_key === 'practice'
                    ? 'No Limit'
                    : (type as any).timer_type === 'sectional'
                    ? 'Sectional Timer'
                    : 'Global Timer'}
                </Badge>
                {(type as any).instructions_html && (
                  <Badge
                    variant="outline"
                    className="hidden sm:inline-flex text-[10px] font-medium bg-primary/10 text-primary border-primary/20 items-center gap-1 px-1.5 py-0.5"
                    title="Special instructions configured for this test"
                  >
                    <Info className="h-3 w-3 text-primary" />
                    <span>Instructions</span>
                  </Badge>
                )}
              </div>

              {/* 3. Combined Single Metrics Tile */}
              <div className="w-full p-2.5 sm:p-3 rounded-xl bg-muted/40 border border-border/60 grid grid-cols-3 divide-x divide-border/60 text-center text-xs">
                <div className="px-1 space-y-0.5">
                  <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Questions</div>
                  <div className="font-bold text-foreground text-xs sm:text-sm">{type.total_questions} Qs</div>
                </div>
                <div className="px-1 space-y-0.5">
                  <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Duration</div>
                  <div className="font-bold text-foreground text-xs sm:text-sm">
                    {type.duration_minutes ? `${type.duration_minutes}m` : 'Untimed'}
                  </div>
                </div>
                <div className="px-1 space-y-0.5">
                  <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Marking</div>
                  <div className="font-bold text-primary text-xs sm:text-sm">
                    +{type.positive_marks} / {type.negative_marks > 0 ? `-${type.negative_marks}` : '0'}
                  </div>
                </div>
              </div>

              {/* 4. Footer Row: Shortened Section Link + Instructions Indicator (mobile) */}
              {((type.sections || []).length > 0 || (type as any).instructions_html) && (
                <div className={`w-full pt-2 border-t border-border/40 flex items-center justify-between text-xs gap-2 ${!(type.sections || []).length ? 'sm:hidden' : ''}`}>
                  {(type.sections || []).length > 0 ? (
                    <div
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleCardSections(type.id ?? type.test_type_key)
                      }}
                      className="inline-flex items-center gap-1 font-semibold text-primary hover:text-primary/80 transition-colors cursor-pointer select-none text-[11px]"
                    >
                      <span>
                        {expandedSectionsMap[type.id ?? type.test_type_key] ? 'Hide' : ''} {type.sections.length} Section{type.sections.length > 1 ? 's' : ''}
                      </span>
                      {expandedSectionsMap[type.id ?? type.test_type_key] ? (
                        <ChevronUp className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5" />
                      )}
                    </div>
                  ) : <div />}

                  {(type as any).instructions_html && (
                    <span
                      title="Special instructions configured for this test"
                      className="sm:hidden inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground shrink-0 cursor-default"
                    >
                      <Info className="h-3.5 w-3.5 text-primary" />
                      <span>Instructions</span>
                    </span>
                  )}
                </div>
              )}

              {/* Collapsible Sections List */}
              {expandedSectionsMap[type.id ?? type.test_type_key] && (type.sections || []).length > 0 && (
                <div className="w-full pl-2.5 border-l-2 border-primary/30 space-y-1.5 animate-in fade-in-50 duration-150">
                  {type.sections.map((s: any, sIdx: number) => (
                    <div key={s.id || sIdx} className="flex items-start justify-between gap-2 text-[11px] leading-snug">
                      <span className="font-medium text-foreground">{s.name}</span>
                      <span className="text-muted-foreground font-mono shrink-0 text-[10px]">
                        {s.total_questions} Qs
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </button>
          )
        })}
      </div>

      {/* Difficulty Level (if applicable to selected type) */}
      {showDifficulty && (
        <div className="space-y-2 pt-2 border-t border-border/50">
          <p className="text-sm font-medium text-foreground">Difficulty Level (Optional)</p>
          <div className="flex flex-wrap gap-2">
            {DIFFICULTY_LEVELS.map(d => (
              <button
                key={d.key}
                id={`difficulty-${d.key}`}
                type="button"
                onClick={() => updateState({ difficultyLevel: state.difficultyLevel === d.key ? null : d.key })}
                className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  state.difficultyLevel === d.key
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card text-muted-foreground border-border hover:border-primary/40'
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Deferred Section Parameters Panel */}
      {activeSelected && (activeSelected.sections || []).some((s: any) => s.scope_deferred) && (
        <div className="space-y-3 pt-4 border-t border-border/50">
          <div>
            <p className="text-sm font-semibold text-foreground">Configure Section Scopes</p>
            <p className="text-xs text-muted-foreground">
              These sections require you to choose a scope before proceeding to question generation.
            </p>
          </div>
          {(activeSelected.sections || []).filter((s: any) => s.scope_deferred).map((sec: any, i: number) => {
            const resolved = state.resolvedSectionParams.find(r => r.sectionId === sec.id) || {}
            const allSubjects = (state.syllabiData || []).flatMap(syl => syl.subjects || [])
            const activeSubject = allSubjects.find(sub => sub.subject_id === resolved.selectedSubjectId)
            const availableChapters = activeSubject?.chapters || []

            return (
              <div key={sec.id || i} className="p-4 rounded-xl border border-border bg-card shadow-glass-xs space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="h-5 w-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold">
                      {i + 1}
                    </span>
                    <span className="text-sm font-medium text-foreground">{sec.name}</span>
                    <span className="text-xs text-muted-foreground font-mono">({sec.total_questions} Qs)</span>
                  </div>
                  <span className="text-[10px] uppercase font-bold text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded">
                    {sec.deferred_scope_type === 'chapter' ? 'Pick Chapter' : 'Pick Subject'}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Subject selector — always shown for deferred sections */}
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Subject *</label>
                    <Select
                      value={resolved.selectedSubjectId ? String(resolved.selectedSubjectId) : '__none__'}
                      onValueChange={val => {
                        const subId = val === '__none__' ? null : Number(val)
                        updateState({
                          resolvedSectionParams: [
                            ...state.resolvedSectionParams.filter(r => r.sectionId !== sec.id),
                            { sectionId: sec.id, selectedSubjectId: subId, selectedChapterId: null }
                          ],
                          filterSubjectIds: subId ? [subId] : []
                        })
                      }}
                    >
                      <SelectTrigger className="w-full text-xs h-auto min-h-9 py-1.5 border-border bg-card text-foreground rounded-lg text-left [&>span]:line-clamp-none [&>span]:whitespace-normal [&>span]:break-words">
                        <SelectValue placeholder="— Select subject —" />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl border-border bg-popover text-foreground shadow-glass-md z-50 w-[var(--radix-select-trigger-width)] min-w-[240px] max-w-[92vw]">
                        <SelectItem value="__none__" className="text-muted-foreground whitespace-normal break-words py-1.5">— Select subject —</SelectItem>
                        {allSubjects.map(sub => (
                          <SelectItem key={sub.subject_id} value={String(sub.subject_id)} className="cursor-pointer py-2 whitespace-normal break-words">
                            <span className="font-semibold text-foreground">{sub.name}</span>
                            <span className="text-muted-foreground text-xs ml-1 font-mono">({sub.questions_count} Qs)</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Chapter selector — only shown when deferred_scope_type is 'chapter' */}
                  {sec.deferred_scope_type === 'chapter' && (
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">Chapter *</label>
                      <Select
                        disabled={!resolved.selectedSubjectId}
                        value={resolved.selectedChapterId ? String(resolved.selectedChapterId) : '__none__'}
                        onValueChange={val => {
                          const chapId = val === '__none__' ? null : Number(val)
                          updateState({
                            resolvedSectionParams: state.resolvedSectionParams.map(r =>
                              r.sectionId === sec.id ? { ...r, selectedChapterId: chapId } : r
                            ),
                            filterChapterIds: chapId ? [chapId] : []
                          })
                        }}
                      >
                        <SelectTrigger className="w-full text-xs h-auto min-h-9 py-1.5 border-border bg-card text-foreground rounded-lg disabled:opacity-50 text-left [&>span]:line-clamp-none [&>span]:whitespace-normal [&>span]:break-words">
                          <SelectValue placeholder="— Select chapter —" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl border-border bg-popover text-foreground shadow-glass-md z-50 w-[var(--radix-select-trigger-width)] min-w-[240px] max-w-[92vw]">
                          <SelectItem value="__none__" className="text-muted-foreground whitespace-normal break-words py-1.5">— Select chapter —</SelectItem>
                          {availableChapters.map(ch => (
                            <SelectItem key={ch.chapter_id} value={String(ch.chapter_id)} className="cursor-pointer py-2 whitespace-normal break-words">
                              <span className="font-semibold text-foreground">{ch.name}</span>
                              <span className="text-muted-foreground text-xs ml-1 font-mono">({ch.questions_count} Qs)</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
